<template>
  <div class="BeneficiaryTypeForm">
    <h2 class="gl_form-title small">
      {{ t('copy_life_beneficiary-type-form_title') }}
    </h2>

    <RadioButtonGroup
      id="another-company"
      :value="beneficiaryType"
      v-model="beneficiaryType"
      :options="beneficiaryTypes"
      :rules="[validationRules.radioRequired]"
    >
      <template #[beneficiaryType] v-if="beneficiaryType">
        <CardInfoComponent
          v-if="message"
          :icon="message.icon"
        >
          {{ message.label }}
        </CardInfoComponent>
      </template>
    </RadioButtonGroup>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onBeforeMount } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import type LifeHiringAnswers from '@/modules/life/hiring/models/APP/LifeHiringAnswers'
  import BENEFICIARY_TYPES from '@/modules/life/hiring/constants/BeneficiaryTypes'
  import LifeBeneficiary from '../../models/APP/LifeBeneficiary'

  const { t } = useI18n()
  const loadingStore = useLoadingStore()
  const { required } = useRules()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: LifeHiringAnswers
    configurations: LifeConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const validationRules = {
    required,
    radioRequired: (value: string) => required(value, 'common.validations.option')
  }

  const beneficiaryTypes: OptionsValuesInterface[] = [
    {
      label: t('copy_life_beneficiary-type-form_option-heirs'),
      value: BENEFICIARY_TYPES.LEGAL
    },
    {
      label: t('copy_life_beneficiary-type-form_options-beneficiaries'),
      value: BENEFICIARY_TYPES.CUSTOM
    }
  ]

  const insured = props.answers.insureds[0]

  const beneficiaryType = ref<string | null>(getInitBeneficiaryType())

  const message = computed(() => {
    if (!beneficiaryType.value) return null
    if (beneficiaryType.value === BENEFICIARY_TYPES.CUSTOM) {
      return props.configurations.tags.myPolicyBeneficiariesAlert
    }

    return props.configurations.tags.heirsPolicyBeneficiariesAlert
  })

  function getInitBeneficiaryType() {
    const isCustom =
      insured.beneficiaries?.some((b) => b.beneficiaryType === BENEFICIARY_TYPES.CUSTOM) ?? false

    return isCustom ? BENEFICIARY_TYPES.CUSTOM : null 
  }

  function beneficiariesSameTypeAsPreviouslySaved(): boolean {
    return insured.beneficiaries.some((b) => b.beneficiaryType === beneficiaryType.value)
  }

  function triggerSubmit() {
    if (!beneficiaryType.value) return
    const nextPageIndex = beneficiaryType.value === BENEFICIARY_TYPES.LEGAL ? 0 : 1
    const page = props.nextForm[nextPageIndex]
    const insureds = props.answers.insureds

    if (!beneficiariesSameTypeAsPreviouslySaved()) {
      const beneficiary = new LifeBeneficiary({
        beneficiaryType: beneficiaryType.value,
        beneficiaryRiskNumber: 1
      })
      insureds[0].beneficiaries = [beneficiary]
    }

    insureds[0].availableToSave = !nextPageIndex
    const answers = { insureds }

    return { page, answers }
  }

  function beforeMount() {
    setTimeout(loadingStore.stopLoading, 300)
  }

  onBeforeMount(beforeMount)
</script>